import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { Reducer } from 'redux';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { put, call, takeLatest } from 'redux-saga/effects';
import * as routerHelpers from '../../router/RouterHelpers';
import { IUser, IMfa, ICheckMfaRes } from '../../pages/auth/interfaces';
import { TAppActions } from '../rootDuck';
import { ActionsUnion, createAction } from '../../utils/action-helper';
import { IServerResponse } from '../../interfaces/server';
import { login, getMfa, checkMfa, changeRefreshToken } from '../../crud/auth.crud';
import { ICheckMfa, ILogin, IRefreshToken } from '../../interfaces/user';
import { getResponseMessage } from '../../utils/utils';

const LOGOUT = 'auth/LOGOUT';

const CLEAR_LOGIN = 'auth/CLEAR_LOGIN';
const LOGIN_REQUEST = 'auth/LOGIN_REQUEST';
const LOGIN_SUCCESS = 'auth/LOGIN_SUCCESS';
const LOGIN_FAIL = 'auth/LOGIN_FAIL';

const MFA_CODE_REQUEST = 'auth/MFA_CODE_REQUEST';
const MFA_CODE_SUCCESS = 'auth/MFA_CODE_SUCCESS';
const MFA_CODE_FAIL = 'auth/MFA_CODE_FAIL';

const MFA_CHECK_REQUEST = 'auth/MFA_CHECK_REQUEST';
const MFA_CHECK_SUCCESS = 'auth/MFA_CHECK_SUCCESS';
const MFA_CHECK_FAIL = 'auth/MFA_CHECK_FAIL';

const REFRESH_TOKEN_REQUEST = 'auth/REFRESH_TOKEN_REQUEST';
const REFRESH_TOKEN_SUCCESS = 'auth/REFRESH_TOKEN_SUCCESS';
const REFRESH_TOKEN_FAIL = 'auth/REFRESH_TOKEN_FAIL';

const SET_EMPTY_ERR_ATTEMPTS = 'auth/SET_EMPTY_ERR_ATTEMPTS';
const SET_USER = 'auth/SET_USER';
const SET_TOKEN = 'auth/SET_TOKEN';

export interface IInitialState {
  user: IUser | undefined;
  authToken: string | undefined;
  refreshToken: string | undefined;
  checkMfa: boolean;
  mfaData: IMfa | null,

  loginLoading: boolean;
  loginSuccess: boolean;
  loginErrors: string | null;

  emptyResErrAttempts: number;

  mfaLoading: boolean;
  mfaSuccess: boolean;
  mfaError: string | null;

  checkLoading: boolean;
  checkSuccess: boolean;
  checkError: string | null;

  refreshLoading: boolean;
  refreshSuccess: boolean;
  refreshError: string | null;
}

const initialState: IInitialState = {
  user: undefined,
  authToken: undefined,
  refreshToken: undefined,
  checkMfa: false,

  loginLoading: false,
  loginSuccess: false,
  loginErrors: null,

  emptyResErrAttempts: 0,

  mfaLoading: false,
  mfaSuccess: false,
  mfaError: null,
  mfaData: null,

  checkLoading: false,
  checkSuccess: false,
  checkError: null,

  refreshLoading: false,
  refreshSuccess: false,
  refreshError: null,
};

export const reducer: Reducer<IInitialState & PersistPartial, TAppActions> = persistReducer(
  { storage, key: 'auth', whitelist: ['user', 'authToken', 'checkMfa'] },
  (state = initialState, action: any) => {
    switch (action.type) {
      case LOGOUT: {
        routerHelpers.forgotLastLocation();
        return initialState;
      }

      // login
      case CLEAR_LOGIN: {
        return { 
          ...state, 
          loginLoading: false, 
          loginErrors: null, 
          loginSuccess: false 
        };
      }

      case LOGIN_REQUEST: {
        return {
          ...state,
          loginLoading: true,
          user: undefined,
          authToken: undefined,
          refreshToken: undefined,
          loginErrors: null,
        };
      }

      case LOGIN_SUCCESS: {
        const {accessToken, refreshToken} = action.payload.tokens
        
        return {
          ...state,
          loginLoading: false,
          loginSuccess: true,
          user: action.payload,
          authToken: accessToken,
          refreshToken,
          loginErrors: null,
        };
      }
      case LOGIN_FAIL: {
        return {
          ...state,
          loginLoading: false,
          loginSuccess: false,
          user: undefined,
          authToken: undefined,
          refreshToken: undefined,
          loginErrors: action.payload,
        };
      }

      case SET_EMPTY_ERR_ATTEMPTS: {
        return {
          ...state,
          emptyResErrAttempts: action.payload,
        };
      }

      // getMfa
      case MFA_CODE_REQUEST: {
        return {
          ...state,
          mfaLoading: true,
          mfaSuccess: false,
          mfaError: null,
        };
      }

      case MFA_CODE_SUCCESS: {        
        return {
          ...state,
          mfaData: action.payload,
          mfaLoading: false,
          mfaSuccess: true,
        };
      }

      case MFA_CODE_FAIL: {
        return {
          ...state,
          mfaLoading: false,
          mfaError: action.payload,
        };
      }

      // CheckMfa
      case MFA_CHECK_REQUEST: {
        return {
          ...state,
          checkLoading: true,
          checkSuccess: false,
          checkError: null,
        };
      }

      case MFA_CHECK_SUCCESS: {     
        console.log('action.payload', action.payload);
                   
        return {
          ...state,
          authToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
          checkLoading: false,
          checkSuccess: true,
          checkMfa: true,
        };
      }

      case MFA_CHECK_FAIL: {
        return {
          ...state,
          checkLoading: false,
          checkError: action.payload,
        };
      }

      // setUser
      case SET_USER: {
        return {
          ...state,
          user: action.payload.data
        };
      }

      // refreshToken
      case REFRESH_TOKEN_REQUEST: {
        return {
          ...state,
          refreshLoading: true,
          refreshSuccess: false,
          refreshError: null,
        };
      }

      case REFRESH_TOKEN_SUCCESS: {    
        console.log('refresh', action.payload);
                    
        return {
          ...state,
          authToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
          refreshLoading: false,
          refreshSuccess: true,
        };
      }

      case REFRESH_TOKEN_FAIL: {
        return {
          ...state,
          refreshLoading: false,
          refreshError: action.payload,
        };
      }

      case SET_TOKEN: {
        return {
          ...state,
          authToken: action.payload,
        }
      }

      default:
        return state;
    }
  }
);

export const actions = {
  logout: () => createAction(LOGOUT),
  // login
  clearLogin: () => createAction(CLEAR_LOGIN),
  loginRequest: (payload: ILogin) =>
    createAction(LOGIN_REQUEST, payload),
  loginSuccess: (payload: IUser) => createAction(LOGIN_SUCCESS, payload),
  loginFail: (payload: string) => createAction(LOGIN_FAIL, payload),

  // for handle empty responses from server, check there _base/utils/utils.js
  setEmptyResErrAttempts: (payload: number) => createAction(SET_EMPTY_ERR_ATTEMPTS, payload),

  // get Mfa code
  fetchMfaRequest: () => createAction(MFA_CODE_REQUEST),
  fetchMfaSuccess: (payload: IServerResponse<IMfa>) =>
    createAction(MFA_CODE_SUCCESS, payload),
  fetchMfaFail: (payload: string) => createAction(MFA_CODE_FAIL, payload),
  
  // check Mfa code
  checkMfaRequest: ({ payload }: { payload: ICheckMfa }) => createAction(MFA_CHECK_REQUEST, payload),
  checkMfaSuccess: (payload: ICheckMfaRes) =>
    createAction(MFA_CHECK_SUCCESS, payload),
  checkMfaFail: (payload: string) => createAction(MFA_CHECK_FAIL, payload),

  setUser: (payload: IUser) => createAction(SET_USER, payload),

  // refreshToken
  refreshTokenRequest: ({ payload }: { payload: string }) => createAction(REFRESH_TOKEN_REQUEST, payload),
  refreshTokenSuccess: (payload: IRefreshToken) =>
    createAction(REFRESH_TOKEN_SUCCESS, payload),
  refreshTokenFail: (payload: string) => createAction(REFRESH_TOKEN_FAIL, payload),

  setAuthToken: (authToken: string) => createAction(SET_TOKEN, authToken),
};

export type TActions = ActionsUnion<typeof actions>;

function* loginSaga({ payload }: { payload: ILogin }) {
  try {
    const { data }: { data: IServerResponse<IUser> } = yield call(() =>
      login(payload)
    );
    yield put(actions.loginSuccess(data.data));
  } catch (e) {
    yield put(actions.loginFail(getResponseMessage(e)));
  }
}

function* getMfaSaga() {
  try {
    const { data }: { data: IServerResponse<IMfa> } = yield call(() =>
      getMfa()
    );
    yield put(actions.fetchMfaSuccess(data));
  } catch (e) {
    yield put(actions.fetchMfaFail(getResponseMessage(e)));
  }
}

function* checkMfaSaga({ payload }: { payload: ICheckMfa }) {
  try {
    const { data }: { data: IServerResponse<ICheckMfaRes> } = yield call(() =>
      checkMfa(payload)
    );
    yield put(actions.checkMfaSuccess(data.data));
  } catch (e) {
    yield put(actions.checkMfaFail(getResponseMessage(e)));
  }
}

function* refreshTokenSaga({ payload }: { payload: string }) {
  try {
    const { data }: { data: IServerResponse<IRefreshToken> } = yield call(() =>
      changeRefreshToken(payload)
    );
    yield put(actions.refreshTokenSuccess(data.data));
  } catch (e) {
    yield put(actions.refreshTokenFail(getResponseMessage(e)));
  }
}

export function* saga() {
  yield takeLatest<ReturnType<typeof actions.loginRequest>>(LOGIN_REQUEST, loginSaga);
  yield takeLatest<ReturnType<typeof actions.fetchMfaRequest>>(MFA_CODE_REQUEST, getMfaSaga);
  yield takeLatest<ReturnType<typeof actions.checkMfaRequest>>(MFA_CHECK_REQUEST, checkMfaSaga);
  yield takeLatest<ReturnType<typeof actions.refreshTokenRequest>>(REFRESH_TOKEN_REQUEST, refreshTokenSaga);
}
